/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axiosWrapper from "../../services/axiosWrapper";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import type {
  DeleteViewResponse,
  CreateViewResponse,
  UpdateViewResponse,
  LayoutData,
} from "@ServerTypes/layoutView/layoutView.schema";
import "./viewCss.css";
import { showToastNotification } from "../../reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "../../services/constants";

import Accordion from "../../cubeComponent/html/accordion/materialUiAccordian/index";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconComponent from "@CubeComponents/icons";
import LayoutRow from "./LayoutRow";
import { LAYOUT_MENU_ACTION } from "@Helpers/constants";
import type { DropdownItem } from "@Helpers/types/layout/types";

// Ids for the header "Create" menu options; the `=== SHARED` checks below also drive the my-vs-shared split.
const CREATE_LAYOUT_OPTION = { MY: "myLayout", SHARED: "sharedLayout" } as const;

interface RootState {
  userDetails: any;
}

interface Props {
  layoutData: LayoutData[];
  currentDataId?: string;
  onClickLayout: (data: any) => void;
  onEditCallback?: (args: any) => void;
  onRenameCallback?: (args: any) => void;
  onDuplicateCallback?: (args: any) => void;
  onDeleteCallback?: (args: any) => Promise<void> | void;
  setLayoutData: React.Dispatch<React.SetStateAction<LayoutData[]>>;
  headerName?: string;
  enableUpdate?: boolean;
  name?: string;
  createNewLayoutData: () => any;
  newData?: Record<string, any>;
  /**
   * When provided, selecting an option from the header "Create" menu ("My Layout" / "Shared Layout")
   * invokes this callback (with `isShared`) instead of the built-in create-on-select flow. Use it to
   * open a custom create modal (e.g. a module picker) that performs the createView call itself.
   * Existing usages omit it and keep the default behavior.
   */
  onCreateClick?: (isShared: boolean) => void;
  /**
   * When provided, returns the dropdown menu items for a given layout row (overrides the default
   * Rename/Duplicate/Delete set). Lets callers vary the menu per row, e.g. shared vs. my layouts.
   * Supported item ids: "edit", "rename", "delete", "duplicate", "moveToShared", "duplicateAsMy".
   */
  getDropDownItems?: (item: LayoutData) => DropdownItem[];
}

function Index({
  layoutData,
  currentDataId,
  onClickLayout,
  onEditCallback,
  onRenameCallback,
  onDuplicateCallback,
  onDeleteCallback,
  setLayoutData,
  headerName = "Layouts",
  enableUpdate = false,
  name = "Layout",
  createNewLayoutData,
  newData = {},
  onCreateClick,
  getDropDownItems,
}: Props) {
  const { t } = useTranslation();
  const [renameData, setRenameData] = useState<any>({});
  const { userDetails } = useSelector((state: RootState) => state);
  const dispatch = useDispatch();

  const [dropDownItems, setdropDownItems] = useState<any[]>([
    {
      text: t("Rename"),
      id: LAYOUT_MENU_ACTION.RENAME,
      iconName: "editProfile",
    },
    {
      text: t("Duplicate"),
      id: LAYOUT_MENU_ACTION.DUPLICATE,
      iconName: "copy",
    },
    {
      text: t("Delete"),
      id: LAYOUT_MENU_ACTION.DELETE,
      iconName: "delete",
    },
  ]);

  function handleSelectedOption({ args, layoutData, layoutDataIndex }: any) {
    if (args.item.id == LAYOUT_MENU_ACTION.EDIT) {
      onEditCallback?.({ layoutData, layoutDataIndex });
    }
    if (args.item.id == LAYOUT_MENU_ACTION.RENAME) {
      setRenameData(layoutData);
    }
    if (args.item.id == LAYOUT_MENU_ACTION.DELETE) {
      onClickOptionDelete({ layoutData, layoutDataIndex });
    }
    if (args.item.id == LAYOUT_MENU_ACTION.DUPLICATE) {
      onClickOptionDuplicate({ layoutData, layoutDataIndex });
    }
    if (args.item.id == LAYOUT_MENU_ACTION.MOVE_TO_SHARED) {
      moveMyLayoutToShared({ layoutData, layoutDataIndex });
    }
    if (args.item.id == LAYOUT_MENU_ACTION.DUPLICATE_AS_MY) {
      copySharedLayoutToMyLayout({ layoutData, layoutDataIndex });
    }
  }

  // Convert a "my" layout into a shared one (keeps it in place, moves it to the Shared section).
  async function moveMyLayoutToShared({
    layoutData,
    layoutDataIndex,
  }: {
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    const response = await axiosWrapper.post<UpdateViewResponse>(`${getBackendUrl()}/layoutView/updateView`, {
      updateData: { isShared: true },
      id: layoutData._id,
    });
    if (response.data.status) {
      setLayoutData((prevState) => {
        const newState = [...prevState];
        if (newState[layoutDataIndex]) {
          newState[layoutDataIndex] = { ...newState[layoutDataIndex], isShared: true };
        }
        return newState;
      });
    }
  }

  // Copy a shared layout into the current user's "my" layouts.
  async function copySharedLayoutToMyLayout({
    layoutData,
    layoutDataIndex,
  }: {
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    const response = await axiosWrapper.post<CreateViewResponse>(`${getBackendUrl()}/layoutView/createView`, {
      createData: { ...layoutData, isShared: false },
    });
    const created = response.data.data;
    if (response.data.status && created) {
      setLayoutData((prevState) => [...prevState, { ...created, createdByData: userDetails }]);
      onDuplicateCallback?.({ duplicatedData: layoutData, layoutDataIndex });
    }
  }

  async function onClickOptionDelete({ layoutData, layoutDataIndex }: any) {
    const response = await axiosWrapper.post<DeleteViewResponse>(`${getBackendUrl()}/layoutView/deleteView`, {
      id: layoutData._id,
    });
    if (response.data.status) {
      setLayoutData((prevState) => {
        const newState = [...prevState];
        newState.splice(layoutDataIndex, 1);
        return newState;
      });
      onDeleteCallback && (await onDeleteCallback({ deleteData: layoutData, layoutDataIndex }));
    }
  }

  async function onClickOptionDuplicate({ layoutData, layoutDataIndex }: any) {
    const response = await axiosWrapper.post<CreateViewResponse>(`${getBackendUrl()}/layoutView/createView`, {
      createData: {
        ...layoutData,
      },
    });
    if (response.data.status) {
      setLayoutData((prevState) => [...prevState, response.data.data!]);
      onDuplicateCallback?.({
        duplicatedData: layoutData,
        layoutDataIndex,
      });
    }
  }

  const updateDropdownItem = {
    text: t("Update"),
    id: LAYOUT_MENU_ACTION.EDIT,
    iconName: "SettingsIcon",
  };

  useEffect(() => {
    if (enableUpdate) {
      setdropDownItems([updateDropdownItem, ...dropDownItems]);
    }
  }, []);

  const createData = (isShared: boolean) => {
    const sharedName = isShared ? "Shared " : "";
    return {
      resourceType: "VIEWER",
      data: createNewLayoutData(),
      name: `${sharedName}${name} ${
        isShared
          ? layoutData.filter((item: any) => item.isShared).length + 1
          : layoutData.filter((item: any) => !item.isShared).length + 1
      }`,
      ...newData,
    };
  };

  const handleTickClick = async () => {
    await axiosWrapper.post<UpdateViewResponse>(`${getBackendUrl()}/layoutView/updateView`, {
      updateData: {
        name: renameData.name,
      },
      id: renameData._id,
    });

    const itemIndex = layoutData.findIndex((item) => item._id === renameData._id);

    setRenameData({});
    setLayoutData((prevState) => {
      if (itemIndex < 0) {
        return prevState;
      }
      const tempData = [...prevState];
      if (!tempData[itemIndex]) {
        return prevState;
      }
      tempData[itemIndex].name = renameData.name;
      return tempData;
    });

    onRenameCallback?.({
      renameData,
      renameDataIndex: itemIndex,
    });
  };

  function layoutContent(showShared: boolean) {
    const layout: any[] = [];
    layoutData.forEach((item: any, index: number) => {
      if (showShared ? item.isShared : !item.isShared) {
        layout.push({
          ...item,
          index,
        });
      }
    });
    return (
      <div className="cursor-pointer font-normal">
        {layout.length > 0 ? (
          layout?.map((item: any) => (
            <LayoutRow
              key={item._id}
              item={item}
              renameData={renameData}
              currentDataId={currentDataId}
              userDetails={userDetails}
              dropDownItems={getDropDownItems ? getDropDownItems(item) : dropDownItems}
              onClickLayout={onClickLayout}
              handleSelectedOption={handleSelectedOption}
              setRenameData={setRenameData}
              onTickClick={handleTickClick}
            />
          ))
        ) : (
          <div className="flex flex-col w- items-center justify-center">
            <IconComponent name="nodatasvg" width="8rem" height="8rem" />
            <div className="text-12">{t("No Data")}</div>
          </div>
        )}
      </div>
    );
  }

  async function onSelectCreate(args: any) {
    let data: any = null;
    const isShared = args.item.id === CREATE_LAYOUT_OPTION.SHARED;

    data = createData(isShared);
    if (data && !Array.isArray(data) && typeof data === "object") {
      const created = await axiosWrapper.post<CreateViewResponse>(`${getBackendUrl()}/layoutView/createView`, {
        createData: { ...data, isShared },
      });
      if (created.data.status) {
        setLayoutData((prevState) => [...prevState, { ...created.data.data!, createdByData: userDetails }]);
        dispatch(
          showToastNotification({
            content: t("designviewer_layout_created", { layoutName: created.data.data!.name }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
    }
  }

  return (
    <div>
      <div
        className="flex justify-between px-2 py-3 mb-4 items-center border-b border-border_light bg-white"
        data-testid="addNewLayout"
      >
        <div className="text-2xl font-semibold leading-[1.2] text-primary_text" data-testid="LayoutsHeading">
          {headerName}
        </div>
        <SelectDropDown
          data-testid="createBtn"
          items={[
            {
              text: "My Layout",
              id: CREATE_LAYOUT_OPTION.MY,
            },
            {
              text: "Shared Layout",
              id: CREATE_LAYOUT_OPTION.SHARED,
            },
          ]}
          select={
            onCreateClick
              ? (args: SelectEventArgs) => onCreateClick(args.item.id === CREATE_LAYOUT_OPTION.SHARED)
              : onSelectCreate
          }
          placeholder={t("Create")}
          buttonType="gradientButton"
        />
      </div>
      <div className="max-h-[25rem] overflow-y-auto">
        <Accordion defaultExpanded data-testid="myLayoutsSection">
          <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="myLayoutsHeader">
            <div
              className="flex justify-between items-center px-2 py-3 text-base font-bold leading-[1.25] text-primary_text"
              data-testid="myLayouts"
            >
              {`My ${headerName}`}
            </div>
          </AccordionSummary>
          <AccordionDetails data-testid="myLayoutsContent">{layoutContent(false)}</AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded data-testid="sharedLayoutsSection">
          <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="sharedLayoutsHeader">
            <div
              className="flex justify-between items-center px-2 py-3 text-base font-bold leading-[1.25] text-primary_text"
              data-testid="sharedLayouts"
            >
              {`Shared ${headerName}`}
            </div>
          </AccordionSummary>
          <AccordionDetails data-testid="sharedLayoutsContent">{layoutContent(true)}</AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
}

export default Index;
